// The theme slider: Video carousel (snippets/block-video-carousel.liquid), Customer reviews and the Slideshow section: slides with arrows, dots, progress bar, mouse drag and an
// optional automatic advance; each video plays/pauses from its play button, shows its time on a timeline you can
// scrub, and has a sound button. Only one video plays at a time.
(() => {
  if (customElements.get('video-carousel')) return;

  class VideoCarousel extends HTMLElement {
    connectedCallback() {
      // Moved in the page after it was set up (e.g. assets/reviews-section.js puts it in its column): only restart what
      // disconnectedCallback stopped; adding the listeners again would make every arrow click move twice.
      if (this.ready) {
        this.resizeObserver.observe(this.track);
        this.pauseObserver?.observe(this);
        this.startAutoplay();
        this.sync();
        return;
      }
      this.track = this.querySelector('[data-vc-track]');
      if (!this.track) return;
      this.ready = true;
      this.index = 0;

      // "Pause when out of view" (Video block): playing videos pause off screen; autoplay ones start again.
      if (this.dataset.pauseOffscreen === 'true' && 'IntersectionObserver' in window) {
        this.pauseObserver = new IntersectionObserver(([entry]) => {
          this.querySelectorAll('video').forEach((video) => {
            if (!entry.isIntersecting && !video.paused) { video.dataset.vcResume = 'true'; video.pause(); }
            else if (entry.isIntersecting && (video.dataset.vcResume === 'true' || video.autoplay)) { delete video.dataset.vcResume; video.play().catch(() => {}); }
          });
        }, { threshold: 0.1 });
        this.pauseObserver.observe(this);
      }

      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-vc-step]');
        const go = event.target.closest('[data-vc-go]');
        const play = event.target.closest('[data-vc-play]');
        const mute = event.target.closest('[data-vc-mute]');
        if (step) this.goTo(this.index + Number(step.dataset.vcStep));
        if (go) this.goTo(Number(go.dataset.vcGo));
        if (mute) this.toggleMute(mute);
        else if (play) this.toggle(play.closest('[data-vc-media]'));
        else if (!this.dragged && event.target.matches('[data-vc-video]')) this.toggle(event.target.closest('[data-vc-media]'));
        this.stopAutoplay();
      });
      this.addEventListener('input', (event) => {
        if (!event.target.matches('[data-vc-timeline]')) return;
        const video = event.target.closest('[data-vc-media]').querySelector('video');
        if (video?.duration) video.currentTime = (Number(event.target.value) / 1000) * video.duration;
      });
      // Touch: a tap shows the timeline and sound button for a moment.
      this.addEventListener('touchstart', (event) => {
        const media = event.target.closest('[data-vc-media]');
        if (!media) return;
        media.classList.add('is-touched');
        clearTimeout(media.touchTimer);
        media.touchTimer = setTimeout(() => media.classList.remove('is-touched'), 2500);
      }, { passive: true });

      this.querySelectorAll('[data-vc-media] video').forEach((video) => {
        const media = video.closest('[data-vc-media]');
        const timeline = media.querySelector('[data-vc-timeline]');
        video.addEventListener('play', () => { media.dataset.state = 'playing'; this.pauseOthers(video); });
        video.addEventListener('pause', () => { media.dataset.state = 'paused'; });
        video.addEventListener('timeupdate', () => {
          if (!timeline || !video.duration) return;
          const share = video.currentTime / video.duration;
          timeline.value = Math.round(share * 1000);
          timeline.style.setProperty('--_played', `${share * 100}%`);
        });
        if (video.autoplay) video.play().catch(() => {});
      });

      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
      }, { passive: true });
      if (this.dataset.drag === 'true') this.enableDrag();
      this.resizeObserver = new ResizeObserver(() => {
        this.sync();
        cancelAnimationFrame(this.roomFrame);
        this.roomFrame = requestAnimationFrame(() => this.room());
      });
      this.resizeObserver.observe(this.track);
      this.startAutoplay();
      if (this.dataset.pauseHover !== 'false') this.addEventListener('pointerenter', () => this.stopAutoplay(true));
      this.addEventListener('pointerleave', () => this.startAutoplay());
      this.sync();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.pauseObserver?.disconnect();
      clearInterval(this.timer);
    }

    // Room above and below the cards for their shadows and hover lift: the track scrolls sideways, so it clips whatever
    // sticks out of it. slider.css pads the track by --vc-room-top/-bottom and takes the space back with a negative
    // margin, so nothing moves. Positions come from the layout (offsetTop), so reveal transforms don't count.
    // A section's CSS adds --vc-lift (hover lift, px) and --vc-hover-scale (e.g. 1.03) on the <video-carousel>.
    room() {
      const track = this.track;
      const y = (el) => {
        let top = 0;
        for (let n = el; n; n = n.offsetParent) top += n.offsetTop + (n.offsetParent?.clientTop || 0);
        return top;
      };
      const css = getComputedStyle(track);
      const start = y(track) + track.clientTop + parseFloat(css.paddingTop);
      const height = track.clientHeight - parseFloat(css.paddingTop) - parseFloat(css.paddingBottom);
      const own = getComputedStyle(this);
      const lift = parseFloat(own.getPropertyValue('--vc-lift')) || 0;
      const scale = parseFloat(own.getPropertyValue('--vc-hover-scale')) || 1;
      let above = 0;
      let below = 0;
      let grow = 0;
      this.slides.forEach((slide) => {
        grow = Math.max(grow, ((scale - 1) / 2) * slide.offsetHeight);
        [slide, ...slide.querySelectorAll('*')].forEach((el) => {
          if (!(el instanceof HTMLElement) || el.offsetParent === null) return;
          const shadow = getComputedStyle(el).boxShadow;
          if (!shadow || shadow === 'none') return;
          const top = y(el) - start;
          const bottom = top + el.offsetHeight - height;
          shadow.split(/,(?![^(]*\))/).forEach((layer) => {
            if (layer.includes('inset')) return;
            const [, dy = 0, blur = 0, spread = 0] = (layer.replace(/[a-z]+\([^)]*\)/g, '').match(/-?[\d.]+px/g) || []).map(parseFloat);
            above = Math.max(above, blur + spread - dy - top);
            below = Math.max(below, bottom + dy + blur + spread);
          });
        });
      });
      // +1px: layout offsets are whole pixels, the drawn cards are not.
      const px = (value) => `${value > 0 ? Math.ceil(value + 1) : 0}px`;
      track.style.setProperty('--vc-room-top', px(above + lift + grow));
      track.style.setProperty('--vc-room-bottom', px(below + grow));
    }

    get slides() {
      return [...this.track.children].filter((slide) => slide.offsetParent !== null);
    }

    // Where the track scrolls to for a slide: its left edge, or centered (data-snap-align="center").
    offset(slide) {
      const left = slide.offsetLeft - this.track.offsetLeft;
      return this.dataset.snapAlign === 'center' ? left - (this.track.clientWidth - slide.offsetWidth) / 2 : left;
    }

    goTo(index) {
      const slides = this.slides;
      let target = (index + slides.length) % Math.max(slides.length, 1);
      // Past the last full view the track can't scroll any further: stop there, and from there "next" starts over
      // (otherwise the automatic advance sits at the end for several turns with the wrong dot lit).
      const last = this.dataset.snapAlign === 'center' ? slides.length - 1 : this.lastStart(slides);
      if (target > last) target = index > this.index && this.index >= last ? 0 : last;
      const slide = slides[target];
      if (!slide) return;
      this.track.scrollTo({ left: this.offset(slide), behavior: 'smooth' });
      // While it glides there, scroll events must not pick the slides it passes (a quick second click would start from
      // the old slide again). Ends when the track gets there, or after a moment (e.g. the visitor took over).
      this.gliding = { left: Math.min(Math.max(this.offset(slide), 0), this.track.scrollWidth - this.track.clientWidth), until: performance.now() + 900 };
      this.setIndex(target);
    }

    stillGliding() {
      if (!this.gliding) return false;
      const wait = this.gliding.until - performance.now();
      if (Math.abs(this.track.scrollLeft - this.gliding.left) > 2 && wait > 0) {
        clearTimeout(this.glideTimer);
        this.glideTimer = setTimeout(() => this.sync(), wait);
        return true;
      }
      this.gliding = null;
      return false;
    }

    // Dots, the counter total and the progress bar follow the slides shown on this device (Liquid prints one per
    // block, also for a slide hidden on desktop or mobile). Nothing changes while every slide shows.
    count(slides) {
      const total = slides.length;
      if (!total || total === this.total || (this.total === undefined && total === this.track.children.length)) return;
      this.total = total;
      this.style.setProperty('--vc-total', total);
      this.querySelectorAll('.vc__dots').forEach((dots) => {
        if (dots.children.length === total) return;
        dots.replaceChildren(...Array.from({ length: total }, (_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'vc__dot';
          dot.dataset.vcGo = i;
          dot.setAttribute('aria-label', `${i + 1} / ${total}`);
          return dot;
        }));
      });
      this.querySelectorAll('.vc__counter > :last-child').forEach((counter) => { counter.textContent = total; });
    }

    sync() {
      const slides = this.slides;
      this.classList.toggle('is-static', this.track.scrollWidth <= this.track.clientWidth + 1);
      if (!slides.length) return;
      this.count(slides);
      if (this.stillGliding()) return;
      const left = this.track.scrollLeft;
      const atEnd = left + this.track.clientWidth >= this.track.scrollWidth - 2;
      let best = 0;
      slides.forEach((slide, i) => {
        if (Math.abs(this.offset(slide) - left) < Math.abs(this.offset(slides[best]) - left)) best = i;
      });
      this.setIndex(atEnd ? this.lastStart(slides) : best, atEnd);
    }

    // The first slide of the last full view: where the track stops scrolling.
    lastStart(slides) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      return Math.max(0, slides.findIndex((slide) => slide.offsetLeft - this.track.offsetLeft >= max - 2));
    }

    setIndex(index, atEnd = false) {
      this.index = index;
      this.style.setProperty('--_index', index);
      this.querySelectorAll('[data-vc-go]').forEach((dot) => dot.setAttribute('aria-current', String(Number(dot.dataset.vcGo) === index)));
      const prev = this.querySelectorAll('[data-vc-step="-1"]');
      const next = this.querySelectorAll('[data-vc-step="1"]');
      // Loop slides (Slideshow): the arrows never switch off; otherwise they do at the ends unless it moves by itself.
      const stops = this.dataset.loop !== 'true' && this.dataset.autoplay === '0';
      prev.forEach((button) => { button.disabled = index === 0 && stops; });
      next.forEach((button) => { button.disabled = atEnd && stops; });
      this.querySelectorAll('[data-vc-current]').forEach((current) => { current.textContent = index + 1; });
    }

    toggle(media) {
      const video = media?.querySelector('video');
      if (!video) return;
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    }

    toggleMute(button) {
      const video = button.closest('[data-vc-media]').querySelector('video');
      if (!video) return;
      video.muted = !video.muted;
      button.setAttribute('aria-pressed', String(video.muted));
    }

    pauseOthers(current) {
      this.querySelectorAll('[data-vc-media] video').forEach((video) => {
        if (video !== current && !video.paused && !video.autoplay) video.pause();
      });
    }

    enableDrag() {
      let startX = 0;
      let startLeft = 0;
      let moved = 0;
      let delta = 0;
      let startIndex = 0;
      const move = (event) => {
        delta = event.clientX - startX;
        moved = Math.abs(delta);
        if (moved > 4) this.classList.add('is-dragging');
        this.track.scrollLeft = startLeft - delta;
      };
      // A flick of 15% of a slide moves one slide in that direction (like the media gallery); a longer drag lands on
      // the nearest slide.
      const end = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        this.dragged = moved > 4;
        this.classList.remove('is-dragging');
        if (this.dragged) {
          const width = this.slides[0]?.offsetWidth || 1;
          const step = moved > width * 0.15 ? (delta < 0 ? 1 : -1) : 0;
          let target = this.index === startIndex ? startIndex + step : this.index;
          if (this.dataset.loop !== 'true') target = Math.min(Math.max(target, 0), this.slides.length - 1); // no wrap without Loop
          this.goTo(target);
        }
        setTimeout(() => { this.dragged = false; }, 0);
      };
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.target.closest('button, input')) return;
        event.preventDefault();
        startX = event.clientX;
        startLeft = this.track.scrollLeft;
        startIndex = this.index;
        this.gliding = null;
        moved = 0;
        delta = 0;
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', end);
      });
    }

    startAutoplay() {
      const seconds = Number(this.dataset.autoplay);
      if (!seconds || this.stopped || window.Shopify?.designMode) return;
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        const playing = [...this.querySelectorAll('[data-vc-media][data-state="playing"]')].length;
        if (!playing) this.goTo(this.index + 1);
      }, seconds * 1000);
    }

    // A click stops the automatic advance for good; hovering only pauses it.
    stopAutoplay(pause = false) {
      clearInterval(this.timer);
      if (!pause) this.stopped = true;
    }
  }

  customElements.define('video-carousel', VideoCarousel);
})();
